// Build-soak engine for the Test Lab.
//
// Walks an experience scene-by-scene on a set of PCs, each PC running its own
// independent cycle loop (a port of BuildTester's soak-pc.cjs / crash-test.cjs
// harnesses into the operator):
//
//   kill → launch (no_auto_restart, scene_index=startScene) → wait until LIVE
//     → dwell in each scene → next → … until the level stops advancing (the
//     final level is learned on the first cycle) → final soak → complete → kill
//     → repeat, round-robining the configured versions
//
// The agent is the source of durable truth: launches use no_auto_restart, so a
// crash writes experiences.experience_crashes + operations.incidents but is
// never auto-relaunched, and the (non-billable) run row records the version.
// The runner invalidates the run row for inconclusive cycles (stuck / boot
// timeout), so the rows that remain are verified cycles and the DB soak report
// (telemetry.test_run_soak_report) matches the live tallies here.
//
// Failure signals, tallied separately per version:
//   CRASH        current_experience disappeared (or was replaced under us by an
//                old agent's crash recovery) after the cycle went LIVE.
//   OFFLINE      /api/status stopped answering entirely — agent or PC gone.
//   STUCK        the level would not advance below the known final level.
//   BOOT_TIMEOUT never went LIVE within the boot budget.

import { createPCAgentClient, PCAgentClient } from './pcAgent';

// The soak survives the Test Lab page unmounting (the loops are plain
// promises), so the live runner is held at module level and the page
// re-attaches to it on remount. A full app reload DOES lose the runner — the
// launched experiences then just sit in their current scene (nothing advances
// or relaunches them); the Test Lab's Stop button on the still-open test run
// cleans them up.
let activeRunner: SoakRunner | null = null;
export function getActiveSoakRunner(): SoakRunner | null {
  return activeRunner;
}
export function setActiveSoakRunner(r: SoakRunner | null) {
  activeRunner = r;
}

export interface SoakPc {
  id: string;
  name: string;
  hosts: (string | undefined | null)[];
}

export interface SoakConfig {
  pcs: SoakPc[];
  experienceId: string;
  versions: string[]; // round-robined per PC; '' = latest installed
  language?: string;
  startScene: number; // scene 0 waits for touch-to-start, so soaks begin at 1+
  dwellSec: number; // dwell in each scene before pressing Next
  finalSoakSec: number; // observation window in the final scene
  maxSteps: number; // safety cap on Next presses per cycle
  cyclesPerPc: number; // 0 = until stopped
  operatorId?: string;
}

export interface SoakEvent {
  ts: number;
  pcName: string;
  kind: string;
  detail: string;
  severity: 'info' | 'warn' | 'error';
}

export interface SoakPcLiveState {
  pcName: string;
  phase: string; // human-readable, e.g. 'dwell L3 "Nebula"'
  cycle: number;
  version: string;
  done: boolean;
}

export interface SoakVersionTally {
  version: string;
  cycles: number; // verified: completed + crashes
  completed: number;
  crashes: number;
  inconclusive: number; // stuck / boot timeout (run rows invalidated)
  launchFails: number;
  offline: number;
  byScene: Record<string, number>; // crashes per scene
}

// Timing (ms unless suffixed) — ported from BuildTester's tuned values.
const POLL_MS = 1500;
const BOOT_TIMEOUT_MS = 120_000;
const BOOT_GRACE_MS = 15_000; // ignore "not running" right after launch
const SCENE_TIMEOUT_MS = 60_000; // per Next press (retried once)
const OFFLINE_AFTER_MS = 20_000; // status unanswered this long ⇒ OFFLINE
const OFFLINE_WAIT_MS = 15 * 60_000; // wait for an offline PC to come back
const PRECLEAN_MS = 2_500;
const BETWEEN_CYCLES_MS = 2_000;
const FAIL_BACKOFF_MS = 6_000;
const STAGGER_MS = 2_000; // offset PC loop starts (avoid forming one MP session)
const GUARD_RETRY_MS = 30_000; // launch refused by sync/diagnostics guard
const GUARD_RETRY_MAX = 20; // ≈10 min of waiting on a busy sync
const MAX_EVENTS = 250;

interface PollResult {
  reached: boolean;
  running: boolean;
  level: number | null;
  scene: string | null;
  loading: boolean;
  pid: number | null;
  runId: string | null;
  uptime: number | null;
}

// 'replaced' = same run relaunched under us (an old agent's crash recovery:
// run_id is reused, only the pid changes) — the crash row exists, we kill the
// relaunch. 'takeover' = a DIFFERENT run appeared (someone launched an
// experience on this PC): not ours, never kill it — the soak backs off.
type WatchOutcome = 'ok' | 'crash' | 'replaced' | 'takeover' | 'offline' | 'timeout' | 'stopping';

export class SoakRunner {
  readonly config: SoakConfig;
  readonly events: SoakEvent[] = [];
  readonly pcStates = new Map<string, SoakPcLiveState>();
  readonly tallies = new Map<string, SoakVersionTally>();
  /** Crash/offline events not yet acknowledged in the UI — drives the alarm. */
  unackedAlerts = 0;
  running = false;

  private stopping = false;
  private listeners = new Set<() => void>();
  private loops: Promise<void>[] = [];
  // Final level learned per version ('' = latest) on the first full cycle, so
  // later cycles skip the two-timeout end-of-experience probe.
  private finalLevelByVersion = new Map<string, number>();

  constructor(config: SoakConfig) {
    this.config = config;
    for (const v of config.versions) {
      this.tallies.set(v, {
        version: v, cycles: 0, completed: 0, crashes: 0,
        inconclusive: 0, launchFails: 0, offline: 0, byScene: {},
      });
    }
    for (const pc of config.pcs) {
      this.pcStates.set(pc.name, { pcName: pc.name, phase: 'waiting', cycle: 0, version: '', done: false });
    }
  }

  subscribe(cb: () => void): () => void {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }

  acknowledgeAlerts() {
    this.unackedAlerts = 0;
    this.notify();
  }

  start() {
    if (this.running) return;
    this.running = true;
    this.loops = this.config.pcs.map((pc, i) => this.pcLoop(pc, i));
    void Promise.allSettled(this.loops).then(() => {
      this.running = false;
      this.notify();
    });
    this.notify();
  }

  /** Signal all loops to wind down; resolves when every loop has exited. */
  async stop(): Promise<void> {
    this.stopping = true;
    this.notify();
    await Promise.allSettled(this.loops);
  }

  private notify() {
    for (const cb of this.listeners) cb();
  }

  private log(pcName: string, kind: string, detail: string, severity: SoakEvent['severity'] = 'info') {
    this.events.unshift({ ts: Date.now(), pcName, kind, detail, severity });
    if (this.events.length > MAX_EVENTS) this.events.length = MAX_EVENTS;
    this.notify();
  }

  private setPhase(pcName: string, phase: string, patch?: Partial<SoakPcLiveState>) {
    const st = this.pcStates.get(pcName);
    if (!st) return;
    Object.assign(st, patch, { phase });
    this.notify();
  }

  private tally(version: string): SoakVersionTally {
    let t = this.tallies.get(version);
    if (!t) {
      t = { version, cycles: 0, completed: 0, crashes: 0, inconclusive: 0, launchFails: 0, offline: 0, byScene: {} };
      this.tallies.set(version, t);
    }
    return t;
  }

  private raiseAlert(pcName: string, kind: string, detail: string) {
    this.unackedAlerts++;
    this.log(pcName, kind, detail, 'error');
  }

  private async sleep(ms: number) {
    let waited = 0;
    while (waited < ms && !this.stopping) {
      const step = Math.min(250, ms - waited);
      await new Promise((r) => setTimeout(r, step));
      waited += step;
    }
  }

  private async poll(client: PCAgentClient): Promise<PollResult> {
    const status = await client.getStatus(); // errors are swallowed to null
    if (!status) {
      return { reached: false, running: false, level: null, scene: null, loading: false, pid: null, runId: null, uptime: null };
    }
    const exp = status.current_experience;
    return {
      reached: true,
      running: !!exp && !exp.is_server,
      level: exp && typeof exp.current_level === 'number' ? exp.current_level : null,
      scene: exp?.scene_name || null,
      loading: !!exp?.is_loading,
      pid: exp?.pid ?? null,
      runId: exp?.run_id ?? null,
      uptime: typeof status.uptime_seconds === 'number' ? status.uptime_seconds : null,
    };
  }

  /**
   * Poll until `predicate` is satisfied, watching for the failure signals the
   * whole time. `tracked` (pid/runId at LIVE) detects an old agent's crash
   * recovery silently relaunching under us: the crash row was already written,
   * so it counts as a crash and the replacement gets killed by the caller.
   */
  private async watchUntil(
    client: PCAgentClient,
    predicate: (r: PollResult) => boolean,
    deadlineMs: number,
    out: { level: number | null; scene: string | null },
    tracked: { pid: number | null; runId: string | null } | null,
    graceUntil = 0
  ): Promise<WatchOutcome> {
    let firstFailAt: number | null = null;
    while (!this.stopping && Date.now() < deadlineMs) {
      const r = await this.poll(client);
      if (r.reached) {
        firstFailAt = null;
        if (r.running) {
          if (r.level != null) {
            out.level = r.level;
            if (r.scene) out.scene = r.scene;
          }
          if (tracked && tracked.runId && r.runId && r.runId !== tracked.runId) return 'takeover';
          if (tracked && tracked.pid != null && r.pid != null && r.pid !== tracked.pid) return 'replaced';
          if (predicate(r)) return 'ok';
        } else if (Date.now() >= graceUntil) {
          return 'crash';
        }
      } else {
        if (firstFailAt == null) firstFailAt = Date.now();
        if (Date.now() - firstFailAt >= OFFLINE_AFTER_MS) return 'offline';
      }
      await this.sleep(POLL_MS);
    }
    return this.stopping ? 'stopping' : 'timeout';
  }

  private sceneKey(scene: string | null, level: number | null): string {
    return scene && scene !== 'undefined' ? scene : `L${level ?? '?'}`;
  }

  private async pcLoop(pc: SoakPc, index: number): Promise<void> {
    const client = createPCAgentClient(pc.hosts);
    await this.sleep(index * STAGGER_MS);
    let vi = index; // start offset so PCs don't all begin on the same version
    let cycles = 0;
    while (!this.stopping && (this.config.cyclesPerPc === 0 || cycles < this.config.cyclesPerPc)) {
      const version = this.config.versions[vi % this.config.versions.length];
      vi++;
      try {
        const counted = await this.runCycle(pc, client, version, cycles + 1);
        if (this.stopping) break;
        if (counted === 'takeover') {
          // Someone launched a different run on this PC (possibly a customer).
          // Leave it completely alone: no kill, no more cycles here.
          this.log(pc.name, 'TAKEOVER', 'another launch took over this PC — soak stops here and leaves it running', 'warn');
          this.setPhase(pc.name, 'taken over by another launch — soak stopped', { done: true });
          return;
        }
        if (counted === 'offline') {
          this.setPhase(pc.name, 'offline — waiting for the PC to come back');
          const back = await this.waitForOnline(client);
          if (!back) {
            this.log(pc.name, 'OFFLINE_GIVEUP', `still unreachable after ${OFFLINE_WAIT_MS / 60000} min — giving up on this PC`, 'error');
            break;
          }
          this.log(pc.name, 'ONLINE_AGAIN', 'PC is answering again — resuming', 'warn');
          await this.sleep(FAIL_BACKOFF_MS);
        }
        cycles++;
      } catch (e: any) {
        this.log(pc.name, 'ERROR', e?.message || String(e), 'error');
        await this.sleep(FAIL_BACKOFF_MS);
      }
    }
    // Wind-down: never leave a soak experience running on the PC.
    try { await client.killExperience(true, this.config.operatorId); } catch { /* best effort */ }
    this.setPhase(pc.name, this.stopping ? 'stopped' : 'finished', { done: true });
  }

  private async waitForOnline(client: PCAgentClient): Promise<boolean> {
    const deadline = Date.now() + OFFLINE_WAIT_MS;
    while (!this.stopping && Date.now() < deadline) {
      const r = await this.poll(client);
      if (r.reached) return true;
      await this.sleep(5_000);
    }
    return false;
  }

  // One full cycle on one PC. Returns 'ok' (verdict tallied), 'offline' (caller
  // waits for the PC to come back), or 'takeover' (someone else's launch owns
  // the PC now — caller stops this PC's loop without killing anything).
  private async runCycle(pc: SoakPc, client: PCAgentClient, version: string, cycleNo: number): Promise<'ok' | 'offline' | 'takeover'> {
    const cfg = this.config;
    const t = this.tally(version);
    const vLabel = version || 'latest';
    this.setPhase(pc.name, 'cleaning up previous run', { cycle: cycleNo, version: vLabel });

    // 1. Clean slate.
    try { await client.killExperience(true, cfg.operatorId); } catch { /* may not be running */ }
    await this.sleep(PRECLEAN_MS);
    if (this.stopping) return 'ok';

    // 2. Launch — waiting out the agent's sync/diagnostics guards (409) rather
    // than failing the cycle: a sync kicking in mid-soak should pause the soak,
    // not waste the night.
    this.setPhase(pc.name, `launching ${vLabel}`);
    let launched = false;
    for (let attempt = 0; attempt <= GUARD_RETRY_MAX && !this.stopping; attempt++) {
      const res = await client.launchExperience({
        experience_id: cfg.experienceId,
        version: version || undefined,
        operator_id: cfg.operatorId,
        language: cfg.language || undefined,
        scene_index: cfg.startScene,
        no_auto_restart: true,
      });
      if (res.success) { launched = true; break; }
      const guard = /sync_in_progress|diagnostics_blocked/.test(res.error || '');
      if (!guard) {
        t.launchFails++;
        this.log(pc.name, 'LAUNCH_FAIL', `${vLabel}: ${res.error || res.message}`, 'warn');
        await this.sleep(FAIL_BACKOFF_MS);
        return 'ok';
      }
      this.setPhase(pc.name, `launch blocked (${res.error}) — retrying in ${GUARD_RETRY_MS / 1000}s`);
      if (attempt === 0) this.log(pc.name, 'LAUNCH_WAIT', `agent guard ${res.error} — waiting it out`, 'warn');
      await this.sleep(GUARD_RETRY_MS);
    }
    if (!launched || this.stopping) {
      if (!launched && !this.stopping) {
        t.launchFails++;
        this.log(pc.name, 'LAUNCH_FAIL', `${vLabel}: guard never cleared`, 'error');
      }
      return 'ok';
    }
    const tLaunch = Date.now();

    // 3. Boot → LIVE (running, level reported, not loading).
    this.setPhase(pc.name, `booting ${vLabel}`);
    const out: { level: number | null; scene: string | null } = { level: null, scene: null };
    const boot = await this.watchUntil(
      client,
      (r) => r.running && r.level != null && !r.loading,
      tLaunch + BOOT_TIMEOUT_MS,
      out, null, tLaunch + BOOT_GRACE_MS
    );
    if (boot === 'stopping') return 'ok';
    if (boot === 'offline') { t.offline++; this.raiseAlert(pc.name, 'OFFLINE', `PC stopped answering during boot (cycle ${cycleNo})`); return 'offline'; }
    if (boot === 'crash' || boot === 'replaced') {
      t.cycles++; t.crashes++;
      t.byScene['startup/load'] = (t.byScene['startup/load'] || 0) + 1;
      this.raiseAlert(pc.name, 'CRASH', `${vLabel} crashed during startup/load (cycle ${cycleNo})`);
      await this.cleanupAfterFailure(client);
      return 'ok';
    }
    if (boot === 'timeout') {
      t.inconclusive++;
      this.log(pc.name, 'BOOT_TIMEOUT', `${vLabel} never went LIVE within ${BOOT_TIMEOUT_MS / 1000}s (cycle ${cycleNo})`, 'warn');
      await this.invalidateAndKill(client);
      return 'ok';
    }

    // LIVE. Track pid/run_id so an unexpected replacement (old agent's crash
    // recovery) is caught during the walk.
    const live = await this.poll(client);
    const tracked = { pid: live.pid, runId: live.runId };
    let level = out.level ?? cfg.startScene;
    let scene = out.scene;
    const bootSec = Math.round((Date.now() - tLaunch) / 1000);
    this.log(pc.name, 'LIVE', `${vLabel} cycle ${cycleNo} booted in ${bootSec}s at L${level} "${this.sceneKey(scene, level)}"`);

    const finalKey = version;
    const knownFinal = this.finalLevelByVersion.get(finalKey);

    // 4. Walk the scenes: dwell (watching for a crash the whole time), then
    // Next, until the known/learned final level.
    for (let step = 0; step < cfg.maxSteps && !this.stopping; step++) {
      if (knownFinal != null && level >= knownFinal) break;

      if (cfg.dwellSec > 0) {
        this.setPhase(pc.name, `dwell ${cfg.dwellSec}s in L${level} "${this.sceneKey(scene, level)}"`);
        const dw = await this.watchUntil(client, () => false, Date.now() + cfg.dwellSec * 1000, out, tracked);
        if (dw === 'stopping') { await client.killExperience(true, cfg.operatorId); return 'ok'; }
        if (dw === 'takeover') return 'takeover';
        if (dw === 'offline') { t.offline++; this.raiseAlert(pc.name, 'OFFLINE', `PC went offline in L${level} (cycle ${cycleNo})`); return 'offline'; }
        if (dw === 'crash' || dw === 'replaced') {
          return this.recordCrash(pc, client, t, vLabel, cycleNo, this.sceneKey(scene, level), `dwell L${level}`, dw === 'replaced');
        }
        level = out.level ?? level; scene = out.scene ?? scene;
      }

      const want = level + 1;
      this.setPhase(pc.name, `advancing L${level} → L${want}`);
      await client.nextLevel();
      let adv = await this.watchUntil(client, (r) => r.level != null && r.level >= want, Date.now() + SCENE_TIMEOUT_MS, out, tracked);
      if (adv === 'timeout') {
        await client.nextLevel();
        adv = await this.watchUntil(client, (r) => r.level != null && r.level >= want, Date.now() + SCENE_TIMEOUT_MS, out, tracked);
      }
      if (adv === 'stopping') { await client.killExperience(true, cfg.operatorId); return 'ok'; }
      if (adv === 'takeover') return 'takeover';
      if (adv === 'offline') { t.offline++; this.raiseAlert(pc.name, 'OFFLINE', `PC went offline advancing to L${want} (cycle ${cycleNo})`); return 'offline'; }
      if (adv === 'crash' || adv === 'replaced') {
        return this.recordCrash(pc, client, t, vLabel, cycleNo, this.sceneKey(scene, level), `transition L${level}→L${want}`, adv === 'replaced');
      }
      if (adv === 'timeout') {
        if (knownFinal == null) {
          // Would not advance twice with no known final ⇒ this IS the final
          // scene. Learn it so later cycles skip this two-timeout probe.
          this.finalLevelByVersion.set(finalKey, level);
          this.log(pc.name, 'FINAL_SCENE', `${vLabel}: L${level} "${this.sceneKey(scene, level)}" is the last scene (learned)`);
          break;
        }
        t.inconclusive++;
        this.log(pc.name, 'STUCK', `${vLabel} stuck at L${level} "${this.sceneKey(scene, level)}" (cycle ${cycleNo})`, 'warn');
        await this.invalidateAndKill(client);
        return 'ok';
      }
      level = out.level ?? level; scene = out.scene ?? scene;
      this.log(pc.name, 'SCENE', `→ L${level} "${this.sceneKey(scene, level)}"`);
    }
    if (this.stopping) { await client.killExperience(true, cfg.operatorId); return 'ok'; }

    // 5. Final soak — the window where late crashes surface.
    if (cfg.finalSoakSec > 0) {
      this.setPhase(pc.name, `final soak ${cfg.finalSoakSec}s in L${level} "${this.sceneKey(scene, level)}"`);
      const sk = await this.watchUntil(client, () => false, Date.now() + cfg.finalSoakSec * 1000, out, tracked);
      if (sk === 'stopping') { await client.killExperience(true, cfg.operatorId); return 'ok'; }
      if (sk === 'takeover') return 'takeover';
      if (sk === 'offline') { t.offline++; this.raiseAlert(pc.name, 'OFFLINE', `PC went offline in the final soak (cycle ${cycleNo})`); return 'offline'; }
      if (sk === 'crash' || sk === 'replaced') {
        return this.recordCrash(pc, client, t, vLabel, cycleNo, this.sceneKey(scene, level), `final soak L${level}`, sk === 'replaced');
      }
    }

    // 6. Complete BEFORE killing: stamps completed_at/total_time_ms on the run
    // row and makes the kill's abort path no-op.
    await client.completeExperience();
    await client.killExperience(false, cfg.operatorId);
    t.cycles++; t.completed++;
    this.log(pc.name, 'CYCLE_OK', `${vLabel} cycle ${cycleNo} completed (L${level} "${this.sceneKey(scene, level)}")`);
    this.setPhase(pc.name, 'cycle complete');
    await this.sleep(BETWEEN_CYCLES_MS);
    return 'ok';
  }

  private recordCrash(
    pc: SoakPc, client: PCAgentClient, t: SoakVersionTally,
    vLabel: string, cycleNo: number, sceneKey: string, phase: string, replaced: boolean
  ): Promise<'ok'> {
    t.cycles++; t.crashes++;
    t.byScene[sceneKey] = (t.byScene[sceneKey] || 0) + 1;
    const note = replaced ? ' (agent relaunched it — old agent build; killing the relaunch)' : '';
    this.raiseAlert(pc.name, 'CRASH', `${vLabel} crashed at "${sceneKey}" during ${phase} (cycle ${cycleNo})${note}`);
    return this.cleanupAfterFailure(client).then(() => 'ok' as const);
  }

  private async cleanupAfterFailure(client: PCAgentClient) {
    try { await client.killExperience(true, this.config.operatorId); } catch { /* best effort */ }
    await this.sleep(BETWEEN_CYCLES_MS);
  }

  // Inconclusive cycle: delete the run row so the DB report's denominator only
  // holds verified cycles (mirrors crash-test.cjs's "verified runs").
  private async invalidateAndKill(client: PCAgentClient) {
    try { await client.invalidateExperienceRun(this.config.operatorId); } catch { /* best effort */ }
    try { await client.killExperience(true, this.config.operatorId); } catch { /* best effort */ }
    await this.sleep(BETWEEN_CYCLES_MS);
  }
}
